import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { toast } from 'react-toastify'
import to from 'await-to-js'

import { TextInput } from 'components/FormUtils/TextInput'
import { TextareaInput } from 'components/FormUtils/TextareaInput'
import { Switch } from 'components/UI/Switch/Switch'
import FbButton from 'components/FbUI/FbButton'
import { handleFormError } from 'utils/handleFormError'

import {
  useGetCampaignSettings,
  useUpdateCampaignSettings,
} from '../campaign_settings_api'

interface SequenceSettingsProps {
  campaignId: number
}

interface SettingsForm {
  name: string
  max_emails_per_day: number
  max_new_leads_per_day: number
  plain_text: boolean
  open_tracking: boolean
  can_unsubscribe: boolean
  unsubscribe_text: string
}

const defaultValues: SettingsForm = {
  name: '',
  max_emails_per_day: 50,
  max_new_leads_per_day: 50,
  plain_text: true,
  open_tracking: false,
  can_unsubscribe: true,
  unsubscribe_text: '',
}

export function SequenceSettings({ campaignId }: SequenceSettingsProps) {
  const { data: settings, isLoading } = useGetCampaignSettings(campaignId)
  const updateSettingsMutation = useUpdateCampaignSettings(campaignId)

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isDirty },
    setError,
    reset,
    watch,
  } = useForm<SettingsForm>({
    defaultValues,
    mode: 'onChange',
  })

  const canUnsubscribe = watch('can_unsubscribe')

  // Reset form when settings load
  useEffect(() => {
    if (settings) {
      reset({
        name: settings.name,
        max_emails_per_day: settings.max_emails_per_day,
        max_new_leads_per_day: settings.max_new_leads_per_day,
        plain_text: settings.plain_text,
        open_tracking: settings.open_tracking,
        can_unsubscribe: settings.can_unsubscribe,
        unsubscribe_text: settings.unsubscribe_text,
      })
    }
  }, [settings, reset])

  const onSubmit = handleSubmit(async (values) => {
    const [err] = await to(updateSettingsMutation.mutateAsync(values))

    if (err) {
      handleFormError(err, setError)
      // Show toast with error message for API errors
      const errorMessage =
        (err as any)?.response?.data?.detail ||
        (err as any)?.message ||
        'Failed to save settings'
      toast.error(errorMessage)
      return
    }

    // Reset form with submitted values to clear dirty state
    reset(values)
    toast.success('Settings saved successfully')
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">Loading settings...</div>
      </div>
    )
  }

  if (!settings) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 mb-2">
            <svg
              className="w-12 h-12 mx-auto"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">
            No Settings Available
          </h3>
          <p className="text-sm text-gray-500">
            Create a sequence first to configure settings.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      <form onSubmit={onSubmit} className="flex-1 overflow-y-auto">
        <div className="space-y-8">
          {/* General Settings and Email Settings - Side by side */}
          <div className="grid grid-cols-2 gap-8">
            {/* General Settings */}
            <section>
              <h3 className="text-sm font-semibold text-gray-900 mb-2 uppercase tracking-wide">
                General Settings
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Configure basic campaign settings.
              </p>
              <div className="space-y-4">
                <TextInput
                  name="name"
                  control={control}
                  label="Campaign Name"
                  placeholder="Enter campaign name"
                  rules={{ required: 'Campaign name is required' }}
                />

                <div className="grid grid-cols-2 gap-4">
                  <TextInput
                    name="max_emails_per_day"
                    control={control}
                    label="Max Emails Per Day"
                    type="number"
                    placeholder="50"
                    rules={{
                      required: 'Required',
                      min: { value: 1, message: 'Must be at least 1' },
                      max: { value: 10000, message: 'Maximum is 10,000' },
                      validate: (value) => {
                        const maxNewLeads = watch('max_new_leads_per_day')
                        if (Number(value) < Number(maxNewLeads)) {
                          return 'Must be greater than or equal to Max New Leads Per Day'
                        }
                        return true
                      },
                    }}
                    preventNegative
                  />
                  <TextInput
                    name="max_new_leads_per_day"
                    control={control}
                    label="Max New Leads Per Day"
                    type="number"
                    placeholder="50"
                    rules={{
                      required: 'Required',
                      min: { value: 1, message: 'Must be at least 1' },
                      max: { value: 1000, message: 'Maximum is 1,000' },
                    }}
                    preventNegative
                  />
                </div>
              </div>
            </section>

            {/* Email Settings */}
            <section>
              <h3 className="text-sm font-semibold text-gray-900 mb-2 uppercase tracking-wide">
                Email Settings
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Configure email format and tracking.
              </p>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <div>
                    <span className="text-sm font-medium text-gray-800">
                      Plain Text Only
                    </span>
                    <p className="text-xs text-gray-500">
                      Send emails as plain text without HTML formatting
                    </p>
                  </div>
                  <Controller
                    name="plain_text"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    )}
                  />
                </div>

                <div className="flex items-center justify-between py-2">
                  <div>
                    <span className="text-sm font-medium text-gray-800">
                      Open Tracking
                    </span>
                    <p className="text-xs text-gray-500">
                      Track when recipients open your emails
                    </p>
                  </div>
                  <Controller
                    name="open_tracking"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    )}
                  />
                </div>
              </div>
            </section>
          </div>

          {/* Unsubscribe Settings - Full width */}
          <section>
            <h3 className="text-sm font-semibold text-gray-900 mb-2 uppercase tracking-wide">
              Unsubscribe Settings
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Configure unsubscribe options for your emails.
            </p>
            <div className="grid grid-cols-2 gap-8">
              <div className="flex items-center justify-between py-2">
                <div>
                  <span className="text-sm font-medium text-gray-800">
                    Allow Unsubscribe
                  </span>
                  <p className="text-xs text-gray-500">
                    Include an unsubscribe link in your emails
                  </p>
                </div>
                <Controller
                  name="can_unsubscribe"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  )}
                />
              </div>

              {canUnsubscribe && (
                <TextareaInput
                  name="unsubscribe_text"
                  control={control}
                  label="Unsubscribe Text"
                  placeholder="Click here to unsubscribe"
                  optional
                />
              )}
            </div>
          </section>
        </div>
      </form>

      {/* Footer with Save Button */}
      <div className="pt-4 border-t border-gray-200 mt-4">
        <FbButton
          type="submit"
          onClick={onSubmit}
          disabled={!isDirty || isSubmitting}
          loading={isSubmitting}
        >
          Save Settings
        </FbButton>
      </div>
    </div>
  )
}
